import type { Driver, Team } from "../../types";
import { playerTeam } from "../../engine/selectors";
import { baseScore, computeStandings } from "../../engine/race";
import {
  directorCarBonus,
  directorChassisAffinity,
  directorDevelopmentPeak,
  directorLuckMult,
  directorSynergyMult,
} from "../../engine/director";
import { historicAffinity } from "../../engine/synergy";
import { BADGES } from "../../data/badges";
import { LUCK_CALM, LUCK_CHAOS } from "../../data/tracks";
import { bind, bindActivate } from "../dom";
import { chassisModel, esc } from "../helpers";
import { badgeArt } from "../badgeArt";
import { helmetIcon, trackIcon, trackIconTitle, weatherIcon, weatherIconTitle } from "../icons";
import { t, type AttrLabel } from "../i18n";
import type { Screen, ScreenContext } from "./context";

/**
 * The player's lineup with synergy applied. Any attribute that received a
 * synergy bonus is rendered green with a ▲ delta and the reasons on hover.
 */
function lineupPanel(team: Team, expanded: boolean): string {
  const T = t();
  const syn = team.synergy;
  const reasonText = syn.reasons.map((r) => T.synReason(r));
  const ch = team.chassis;
  // A home-constructor director develops the car (+motor/+aero) — shown green
  // on the chassis stats, same as a synergy bonus.
  const car = directorCarBonus(team.director, ch);
  const synWhy = `${T.fromSynergy}${reasonText.length ? " · " + reasonText.join("; ") : ""}`;
  const stat = (a: AttrLabel, base: number, delta: number, why: string): string => {
    const eff = base + delta;
    if (delta > 0) {
      const tip = `${a.full} · +${delta} · ${why}`;
      return `<span class="boost" title="${esc(tip)}">${a.abbr} <b>${eff}</b><span class="up">▲${delta}</span></span>`;
    }
    return `<span title="${esc(a.full)}">${a.abbr} <b>${eff}</b></span>`;
  };
  const driverSlot = (d: Driver): string => {
    // Per-driver: only the driver who actually raced this chassis carries the
    // historic-drive pace/consistency edge — the teammate shows none.
    const aff = historicAffinity(d, ch);
    return `<div class="slot">
        <div class="lbl">${T.driver}</div>
        <div class="val">${esc(d.name)} ${esc(d.team)}</div>
        <div class="cstats" style="justify-content:center;margin-top:5px">${stat(T.attrs.pace, d.pace, aff.pace, T.fromHistoric)}${stat(T.attrs.consistency, d.consistency, syn.consistency + aff.consistency, synWhy)}${stat(T.attrs.rain, d.rain, 0, "")}</div>
      </div>`;
  };
  const motorWhy = car.motor > 0 ? (syn.motor > 0 ? `${synWhy} · ${T.fromCarDev}` : T.fromCarDev) : synWhy;
  const chassisSlot = `<div class="slot">
        <div class="lbl">${T.chassis}</div>
        <div class="val">${esc(ch.team)} ${esc(chassisModel(ch))}</div>
        <div class="cstats" style="justify-content:center;margin-top:5px">${stat(T.attrs.motor, ch.motor, syn.motor + car.motor, motorWhy)}${stat(T.attrs.aero, ch.aero, car.aero, T.fromCarDev)}</div>
      </div>`;
  // Director stat: green when it helps the team, red when it's a cost
  // (an aggressive director's negative reliability).
  const dir = team.director;
  const dstat = (a: AttrLabel, n: number, key: "reliability" | "development"): string => {
    const cls = n > 0 ? "boost" : n < 0 ? "neg" : "";
    return `<span class="${cls}" title="${esc(T.directorAttrTip(key))}">${a.abbr} <b>${n}</b></span>`;
  };
  const directorSlot = dir
    ? `<div class="slot">
        <div class="lbl">${T.director}</div>
        <div class="val">${esc(T.directorName(dir.name))} <span style="color:var(--muted);font-weight:400">${esc(dir.team ?? T.directorIndependent)}</span></div>
        <div class="cstats" style="justify-content:center;margin-top:5px">${dstat(T.directorAttrs.reliability, dir.reliability, "reliability")}${dstat(T.directorAttrs.development, directorDevelopmentPeak(dir), "development")}</div>
      </div>`
    : "";
  const reasons = reasonText.length
    ? `<div class="synrow" style="margin-top:8px">${reasonText
        .map((r) => `<span class="syntag">◇ ${esc(r)}</span>`)
        .join("")}</div>`
    : `<p class="hint" style="margin-top:6px">${T.noSynergies}</p>`;
  // The director's headline effect as a tag. An underdog whose condition isn't
  // met (too many mythics) is shown muted — its synergy boost isn't firing.
  const directorRow = dir
    ? (() => {
        const inactiveUnderdog = dir.signature.kind === "underdog" && directorSynergyMult(dir, team) <= 1;
        const style = inactiveUnderdog ? ' style="opacity:.5"' : "";
        const tags = [
          `<span class="syntag"${style} title="${esc(T.directorTraitTip(dir.signature))}">◇ ${esc(T.directorTrait(dir.signature))}</span>`,
        ];
        // Home/independent affinity — the same edge previewed in the director
        // pack, kept visible here so the player remembers their car is in its
        // element (constructor principal on home machinery) or that an
        // independent gels on any chassis. Silent when there's no affinity
        // (a constructor director on a foreign chassis → ×1).
        const affPct = Math.round((directorChassisAffinity(dir, ch) - 1) * 100);
        if (affPct > 0) {
          tags.push(
            `<span class="syntag" title="${esc(T.directorAffinityTip(dir.team !== null))}">${esc(T.directorPreviewAffinity(affPct, dir.team !== null))}</span>`,
          );
        }
        return `<div class="synrow" style="margin-top:6px">${tags.join("")}</div>`;
      })()
    : "";
  // Collapsed by default: a single compact line, so the race result table
  // below doesn't get pushed off-screen on a phone. Full stat cards and
  // synergy reasons are one tap away via the toggle.
  const summary = `<p class="lineupSummary">${esc(team.d1.name)} · ${esc(team.d2.name)} · ${esc(ch.team)} ${esc(chassisModel(ch))}${dir ? ` · ${esc(T.directorName(dir.name))}` : ""}</p>`;
  const details = expanded
    ? `<div class="slots" style="margin-top:10px">${driverSlot(team.d1)}${driverSlot(team.d2)}${chassisSlot}${directorSlot}</div>
      ${reasons}
      ${directorRow}`
    : "";
  return `<div style="margin-top:18px">
      <div class="row" style="justify-content:space-between">
        <h2 style="font-size:15px">${T.yourLineup}</h2>
        <button class="btn ghost sm" id="lineupToggle">${expanded ? T.lineupHide : T.lineupShow}</button>
      </div>
      ${expanded ? `<span class="hint">${T.lineupHint}</span>` : summary}
      ${details}
    </div>`;
}

/** The race screen: standings, the last race's reveal, and the next-GP preview. */
export function renderSeason(ctx: ScreenContext): Screen {
  const s = ctx.state.season!;
  const view = ctx.view;
  const T = t();
  const dots = s.calendar
    .map((_, i) => `<div class="dot ${i < s.round ? "done" : i === s.round ? "cur" : ""}"></div>`)
    .join("");
  const standings = [...s.opponents].sort((a, b) => b.points - a.points);
  const standRows = standings
    .map(
      (team, i) =>
        `<tr class="${team.isPlayer ? "you" : ""}"><td class="pos">${i + 1}</td><td>${esc(team.name)}</td><td style="font-size:11px;color:var(--muted)">${esc(team.d1.name)} / ${esc(team.d2.name)} · ${esc(team.chassis.team)}</td><td class="num">${team.points}</td></tr>`,
    )
    .join("");
  let last = "";
  const lastResult = s.results[s.results.length - 1];
  if (lastResult) {
    const finishers = lastResult.order.filter((e) => !e.dnf);
    const dnfs = lastResult.order.filter((e) => e.dnf);
    // Full classification order: finishers first, then every retirement.
    const field = [...finishers, ...dnfs];
    // Only show the top 14 — the rest are summarized below (or, if the
    // player's own car is outside the top 14, called out individually).
    const TOP_N = 14;
    const visible = field.slice(0, TOP_N);
    const hiddenPlayers = field.slice(TOP_N).filter((e) => e.isPlayer);
    // First time this race's result is shown, reveal it bottom-to-top —
    // last place first, building suspense up to the podium. A re-render of
    // an already-revealed result (tab switch, language change) skips the
    // animation and shows the final state immediately.
    const alreadyRevealed = view.revealedRaces >= s.results.length;
    const STEP = 0.1;
    const row = (e: (typeof visible)[number], i: number): string => {
      const podium = !e.dnf && e.position && e.position <= 3 ? ` podium-${e.position}` : "";
      const revealCls = alreadyRevealed ? "" : " reveal-row";
      const delay = alreadyRevealed ? "" : ` style="animation-delay:${(visible.length - 1 - i) * STEP}s"`;
      const cls = `${e.isPlayer ? "you" : ""}${podium}${revealCls}`.trim();
      const mark = e.isPlayer ? helmetIcon : "";
      return `<tr class="${cls}"${delay}><td class="pos">${e.dnf ? `<span class="dnf">${T.dnf}</span>` : e.position}</td><td>${mark}${esc(e.driverName)}</td><td style="color:var(--muted)">${esc(e.teamName)}</td><td class="num">${e.points || ""}</td></tr>`;
    };
    const rows = visible.map(row).join("");
    // The player's own row(s) outside the top 14 reveal only once the
    // whole table (podium included) has finished animating in.
    const afterTableDelay = visible.length * STEP + 0.3;
    const yourRows = hiddenPlayers
      .map((e, i) => {
        const cls = alreadyRevealed ? "yourpos" : "yourpos reveal-row";
        const style = alreadyRevealed ? "" : ` style="animation-delay:${afterTableDelay + i * STEP}s"`;
        const pos = e.dnf ? T.dnf : `P${e.position}`;
        return `<div class="${cls}"${style}>${helmetIcon}${T.yourResult(pos, `${esc(e.driverName)}`, e.points)}</div>`;
      })
      .join("");
    view.revealedRaces = s.results.length;
    const retire = dnfs.length
      ? `<div class="dnfrow">${dnfs
          .map(
            (e) =>
              `<span class="dnftag">${T.dnf} · ${esc(e.driverName)} <span style="color:var(--dim)">(${esc(e.teamName)})</span></span>`,
          )
          .join("")}</div>`
      : "";
    last = `<div style="margin-top:20px">
        <div class="racehead"><span class="track">${esc(lastResult.track.name)}</span><span class="weights"><span title="${esc(trackIconTitle(lastResult.track.weights, T))}">${trackIcon(lastResult.track.weights)}</span> ${esc(T.trackLabel(lastResult.track.label))} · <span title="${esc(weatherIconTitle(lastResult.weather, T))}">${weatherIcon(lastResult.weather)}</span> ${esc(T.weather(lastResult.weather))}</span></div>
        ${retire}
        <table><tr><th>${T.thPosShort}</th><th>${T.thEntry}</th><th>${T.thConstructor}</th><th class="num">${T.thPts}</th></tr>${rows}</table>
        ${yourRows}</div>`;
  }
  // Live driver standings, aggregated from every race run so far (full field).
  const driverRows = computeStandings(s)
    .drivers.map(
      (d, i) =>
        `<tr class="${d.isPlayer ? "you" : ""}"><td class="pos">${i + 1}</td><td>${esc(d.driverName)}</td><td style="color:var(--muted)">${esc(d.teamName)}</td><td class="num">${d.points}</td></tr>`,
    )
    .join("");
  const standingsTable =
    view.seasonTab === "wcc"
      ? `<table><tr><th>${T.thPos}</th><th>${T.thConstructor}</th><th>${T.thLineup}</th><th class="num">${T.thPts}</th></tr>${standRows}</table>`
      : `<table><tr><th>${T.thPos}</th><th>${T.thDriver}</th><th>${T.thConstructor}</th><th class="num">${T.thPts}</th></tr>${driverRows}</table>`;
  const player = playerTeam(s);
  const lineup = player ? lineupPanel(player, view.lineupExpanded) : "";
  // Multi-step badges (target > 1) get a live progress bar here; one-shot
  // unlocks (target === 1) just fire a toast the moment they complete.
  const activeChallenges = BADGES.filter((b) => b.target > 1 && !ctx.state.unlockedBadges.includes(b.id));
  const challenges = activeChallenges.length
    ? `<div style="margin-top:14px">
        <div class="hint">${T.challengesTitle}</div>
        ${activeChallenges
          .map((b) => {
            const cur = Math.min(b.target, b.progress(s));
            const pct = Math.round((cur / b.target) * 100);
            return `<div class="challenge">
              <div class="challengeRow">
                <span class="challengeName">${esc(T.badgeName(b.id))}</span>
                <span class="challengeProgress">${esc(T.badgeProgress(cur, b.target))}</span>
              </div>
              <div class="challengeBar"><div class="challengeFill" style="width:${pct}%"></div></div>
            </div>`;
          })
          .join("")}
      </div>`
    : "";
  // Once standings are final there's no next race — stay here so the last
  // race's reveal/podium can play, and let the player advance manually.
  const seasonDone = !!s.standings;
  const next = s.calendar[s.round];
  // The projected luck swing reflects the player's director: a gambler/aggressive
  // principal widens it, a reliable one narrows it — mirroring entryScore's
  // effLuck so the previewed ±% matches what actually happens in the race.
  const baseLuck = s.chaos ? LUCK_CHAOS : LUCK_CALM;
  const effLuck = Math.min(0.9, Math.max(0, baseLuck * directorLuckMult(player?.director ?? null)));
  const swingPct = Math.round(effLuck * 100);
  const nextGpHead =
    !seasonDone && next
      ? `<div class="racehead" style="margin-top:8px">
        <div><div class="lbl" style="font-size:10px;color:var(--dim);text-transform:uppercase">${T.nextGp}</div><span class="track">${esc(next.name)}</span></div>
        <span class="weights"><span title="${esc(trackIconTitle(next.weights, T))}">${trackIcon(next.weights)}</span> ${esc(T.trackLabel(next.label))}${
          s.weather[s.round]
            ? ` · <span title="${esc(weatherIconTitle(s.weather[s.round]!, T))}">${weatherIcon(s.weather[s.round]!)}</span> ${esc(T.weather(s.weather[s.round]!))}`
            : ""
        }</span>
      </div>`
      : "";
  const projected =
    !seasonDone && next && player
      ? `<p class="hint" style="margin-top:6px">${T.projected(
          esc(next.name),
          `<b style="color:var(--txt)">${esc(player.d1.name)}</b>`,
          Math.round(baseScore(player.d1, player, next, s.weather[s.round] ?? "dry", s.round, s.calendar.length)),
          `<b style="color:var(--txt)">${esc(player.d2.name)}</b>`,
          Math.round(baseScore(player.d2, player, next, s.weather[s.round] ?? "dry", s.round, s.calendar.length)),
          swingPct,
          s.chaos,
        )}</p>`
      : "";
  const actions = seasonDone
    ? `<div style="margin:14px 0"><button class="btn primary" id="viewResult">${T.viewChampionship}</button></div>`
    : `<div style="margin:14px 0"><button class="btn primary" id="race">${esc(T.runGp(next!.name))}</button>
      <button class="btn ghost" id="abandon">${s.daily ? T.forfeit : T.abandon}</button></div>`;
  const unlockBanner = view.justUnlocked.length
    ? `<div class="unlockBanner">${view.justUnlocked
        .map((id) => {
          const art = badgeArt(id);
          const icon = art ? `<img class="bannerArt" src="${art}" alt="" width="32" height="32" />` : "🏅";
          return `<span>${icon} ${esc(T.badgeUnlocked(T.badgeName(id)))}</span>`;
        })
        .join("")}</div>`
    : "";
  const html = `<div class="fade">
      <h2>${T.seasonRound(Math.min(s.round + 1, s.calendar.length))}</h2>
      <div class="progress">${dots}</div>
      ${unlockBanner}
      ${nextGpHead}
      ${projected}
      ${actions}
      ${lineup}
      ${challenges}
      ${last}
      <h2 style="margin-top:26px;font-size:15px">${T.standingsTitle}</h2>
      <div class="tabs" role="group" aria-label="${esc(T.standingsTitle)}">
        <div class="tab ${view.seasonTab === "wcc" ? "active" : ""}" id="sTabWcc" role="button" tabindex="0" aria-pressed="${view.seasonTab === "wcc"}">${T.tabConstructors}</div>
        <div class="tab ${view.seasonTab === "wdc" ? "active" : ""}" id="sTabWdc" role="button" tabindex="0" aria-pressed="${view.seasonTab === "wdc"}">${T.tabDrivers}</div>
      </div>
      ${standingsTable}
    </div>`;
  return {
    html,
    bind() {
      if (seasonDone) {
        bind("viewResult", "click", () => ctx.actions.onViewResult());
      } else {
        bind("race", "click", () => ctx.actions.onRace());
        bind("abandon", "click", () => ctx.actions.abandonSeason());
      }
      bindActivate("sTabWcc", () => {
        view.seasonTab = "wcc";
        ctx.rerender();
      });
      bindActivate("sTabWdc", () => {
        view.seasonTab = "wdc";
        ctx.rerender();
      });
      bind("lineupToggle", "click", () => {
        view.lineupExpanded = !view.lineupExpanded;
        ctx.rerender();
      });
    },
  };
}
